import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { DesireIcon, PrivateIcon, ChosenIcon, SharedGardenIcon, PublicIcon } from "../components/GardenIcons";
import AudienceSelector, { AudienceOption } from "../components/AudienceSelector";
import FriendSelector from "../components/FriendSelector";
import { GARDEN_DESIRE_CATEGORIES, DESIRE_LEVELS, DESIRE_LEVEL_I18N_KEY, DesireLevel } from "../utils/gardenDesireTags";

// Mes Désirs — pas une liste d'entrées mais un profil de préférences : chaque tag cycle entre les
// 4 niveaux au clic, jamais de couleur par option (uniquement noir/blanc/gris, un badge plus foncé
// = plus engagé), exactement l'esprit "élégant, adulte, jamais vulgaire" demandé.
const LEVEL_STYLE: Record<DesireLevel | "none", { bg: string; color: string; border: string }> = {
  none: { bg: "white", color: "#999", border: "1px solid #E0E0E0" },
  like: { bg: "#0a0a0a", color: "white", border: "1px solid #0a0a0a" },
  curious: { bg: "#555", color: "white", border: "1px solid #555" },
  discover: { bg: "#F0F0F0", color: "#0a0a0a", border: "1px solid #ccc" },
  private: { bg: "white", color: "#ccc", border: "1px dashed #ccc" },
};

export default function GardenDesires() {
  const navigate = useNavigate();
  const { lang } = useApp();
  const [tags, setTags] = useState<Record<string, DesireLevel>>({});
  const [freeText, setFreeText] = useState("");
  const [audience, setAudience] = useState("private");
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [gardenId, setGardenId] = useState<string>("");
  const [myGardens, setMyGardens] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/garden/me/desires").then(d => {
      const desires = d?.desires;
      if (desires) {
        setTags(desires.tags || {}); setFreeText(desires.free_text || ""); setAudience(desires.audience || "private");
        setSharedWith(desires.shared_with || []);
        if (desires.garden_id) setGardenId(desires.garden_id);
      }
    });
    api.get("/garden/gardens/mine").then(d => {
      const active = (d?.gardens || []).filter((g: any) => g.my_status === "active");
      setMyGardens(active);
      setGardenId(prev => prev || (active.length === 1 ? active[0].id : ""));
    }).catch(() => {});
  }, []);

  const cycle = (tagKey: string) => {
    const idx = DESIRE_LEVELS.indexOf(tags[tagKey]);
    const next = idx === -1 ? DESIRE_LEVELS[0] : idx === DESIRE_LEVELS.length - 1 ? undefined : DESIRE_LEVELS[idx + 1];
    setTags(prev => {
      const copy = { ...prev };
      if (next) copy[tagKey] = next; else delete copy[tagKey];
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/garden/me/desires", { tags, free_text: freeText, audience, shared_with: sharedWith, garden_id: audience === "garden" ? gardenId : undefined });
      navigate(-1);
    } finally { setSaving(false); }
  };

  // Même parité de partage que les autres sections désormais (garden_desire_shares, ajouté
  // 2026-09-27) : privé / personnes choisies / Notre Jardin / public.
  const audienceOptions: AudienceOption[] = [
    { key: "private", label: t(lang, "garden_audience_private"), sub: "", icon: PrivateIcon },
    { key: "chosen", label: t(lang, "garden_audience_chosen"), sub: "", icon: ChosenIcon },
    ...(myGardens.length > 0 ? [{ key: "garden", label: t(lang, "garden_audience_garden"), sub: "", icon: SharedGardenIcon }] : []),
    { key: "public", label: t(lang, "garden_audience_public"), sub: "", icon: PublicIcon },
  ];

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate("/garden")} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
        <DesireIcon size={18} color="#0a0a0a" />
        <span style={{ fontSize: 20, fontWeight: 700 }}>{t(lang, "garden_cat_desires")}</span>
      </div>

      {GARDEN_DESIRE_CATEGORIES.map(cat => (
        <div key={cat.key} style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>{t(lang, cat.i18nTitleKey)}</div>
          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
            {cat.tags.map(tag => {
              const level = tags[tag.key];
              const s = LEVEL_STYLE[level || "none"];
              return (
                <button key={tag.key} onClick={() => cycle(tag.key)} title={level ? t(lang, DESIRE_LEVEL_I18N_KEY[level]) : ""} style={{
                  padding: "8px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                  backgroundColor: s.bg, color: s.color, border: s.border,
                }}>
                  {lang === "fr" ? tag.labelFr : tag.labelEn}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" as const }}>
        {DESIRE_LEVELS.map(lvl => (
          <div key={lvl} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#999" }}>
            <span style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: LEVEL_STYLE[lvl].bg, border: LEVEL_STYLE[lvl].border }} />
            {t(lang, DESIRE_LEVEL_I18N_KEY[lvl])}
          </div>
        ))}
      </div>

      <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>{t(lang, "garden_desires_free_text_label")}</label>
      <textarea
        value={freeText} onChange={e => setFreeText(e.target.value)} rows={4}
        placeholder={t(lang, "garden_desires_free_text_placeholder")}
        style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, marginBottom: 20, boxSizing: "border-box", resize: "vertical" as const }}
      />

      <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Confidentialité</label>
      <AudienceSelector value={audience} onChange={setAudience} options={audienceOptions} />
      {audience === "chosen" && <FriendSelector selected={sharedWith} onToggle={id => setSharedWith(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])} />}
      {audience === "garden" && myGardens.length > 1 && (
        <select value={gardenId} onChange={e => setGardenId(e.target.value)} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, marginBottom: 16, boxSizing: "border-box" }}>
          {myGardens.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      )}

      <button onClick={save} disabled={saving} style={{ width: "100%", padding: 12, borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer", marginTop: 8 }}>
        {t(lang, "garden_save")}
      </button>
    </div>
  );
}
